import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Boxes,
  Clock,
  Code2,
  Compass,
  Gauge,
  HeartHandshake,
  LifeBuoy,
  Mail,
  MapPin,
  PenTool,
  Quote,
  Rocket,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Users,
} from 'lucide-react';
import Nav from './Nav';
import Footer from './Footer';
import Reveal from './Reveal';
import SectionHeading from './SectionHeading';
import HeroVisual from './HeroVisual';
import CookieConsent from './CookieConsent';
import { company } from '../data/company';

const PILLARS = [
  {
    icon: Users,
    title: 'Expert development team',
    body: 'Designers and engineers who have shipped to the stores before, working as one group on your product rather than passing it between departments.',
  },
  {
    icon: HeartHandshake,
    title: 'Customer-centric approach',
    body: 'A single point of contact, plain-language updates and a plan you can actually read. Your priorities set the order of work, not ours.',
  },
  {
    icon: Sparkles,
    title: 'Innovative solutions',
    body: 'Current platform capabilities used where they earn their place — and deliberately avoided where they would only add weight to your app.',
  },
];

const SERVICES = [
  {
    icon: PenTool,
    name: 'Design',
    lede:
      'We turn an idea into something you can hold: flows people understand on first use, and an interface that still looks considered on the fifth screen.',
    items: [
      'Product discovery, user flows and information architecture',
      'UI design for iOS and Android, honouring each platform',
      'Interactive prototypes for testing before a line of code',
      'Design systems, components and handover documentation',
      'Accessibility, localisation and store presentation assets',
    ],
  },
  {
    icon: Code2,
    name: 'Engineering',
    lede:
      'Software written to be read again. Clear architecture, tests where they matter, and a release process your team can run without us in the room.',
    items: [
      'Native iOS in Swift and SwiftUI',
      'Native Android in Kotlin and Jetpack Compose',
      'Cross-platform builds with React Native or Flutter',
      'APIs, integrations and cloud backends',
      'CI/CD, automated QA, performance and crash budgets',
    ],
  },
  {
    icon: Compass,
    name: 'Consultancy',
    lede:
      'Sometimes the useful thing is not more code but a clear read on where you stand. We review, advise and hand back decisions you can defend.',
    items: [
      'Technical due diligence and code audits',
      'Architecture and platform reviews with a written verdict',
      'Roadmap shaping, scoping and realistic estimates',
      'Team mentoring, code review and hiring support',
      'Store compliance and privacy-by-design guidance',
    ],
  },
];

const APPROACH = [
  {
    step: '01',
    title: 'Discover',
    body: 'A short, focused engagement to understand the audience, the constraints and what success actually means. You leave with a scope, not a mystery.',
  },
  {
    step: '02',
    title: 'Shape',
    body: 'Flows, prototypes and technical decisions in parallel, so design and architecture agree before either becomes expensive to change.',
  },
  {
    step: '03',
    title: 'Build',
    body: 'Short iterations with a working build at the end of each. You see progress on a device, not in a status document.',
  },
  {
    step: '04',
    title: 'Launch',
    body: 'Store submission, review handling, staged rollout and the monitoring needed to catch a problem before your users report it.',
  },
  {
    step: '05',
    title: 'Sustain',
    body: 'OS updates, dependency hygiene, performance work and the next set of features — with the option to take it fully in-house.',
  },
];

const PLATFORMS = [
  {
    icon: Smartphone,
    title: 'Mobile',
    body: 'iOS and Android, native or cross-platform — chosen for your case, not our habit.',
    tags: ['Swift', 'SwiftUI', 'Kotlin', 'Compose', 'React Native', 'Flutter'],
  },
  {
    icon: Boxes,
    title: 'Backend & cloud',
    body: 'The services your app leans on: APIs, auth, storage, notifications and analytics.',
    tags: ['TypeScript', 'Node.js', 'REST & GraphQL', 'Firebase', 'AWS'],
  },
  {
    icon: Gauge,
    title: 'Quality & release',
    body: 'Automation that keeps every build shippable and every regression visible early.',
    tags: ['CI/CD', 'Automated QA', 'Crash reporting', 'Performance budgets'],
  },
  {
    icon: ShieldCheck,
    title: 'Trust & compliance',
    body: 'Privacy considered from the first diagram, through to store review requirements.',
    tags: ['GDPR by design', 'App Store review', 'Google Play policy', 'Data mapping'],
  },
];

const MARQUEE = [
  'Swift',
  'SwiftUI',
  'Kotlin',
  'Jetpack Compose',
  'React Native',
  'Flutter',
  'TypeScript',
  'Node.js',
  'GraphQL',
  'Firebase',
  'AWS',
  'Figma',
  'CI/CD',
  'App Store',
  'Google Play',
];

/**
 * Real client quotes go here — one object per testimonial and the section
 * renders itself. Left empty on purpose: nothing invented ships as a client
 * endorsement.
 */
const TESTIMONIALS: { quote: string; author: string; role: string }[] = [];

const HomePage: React.FC = () => {
  const { hash } = useLocation();

  // Support /#services style links arriving from other pages.
  React.useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 120);
  }, [hash]);

  return (
    <div className="relative min-h-screen bg-ink-950">
      <Nav />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="texture-field relative overflow-hidden pt-[72px]">
        <div className="bloom -left-40 top-0 h-[560px] w-[560px] bg-peri-600/20 animate-drift" />
        <div className="bloom -right-24 top-40 h-[420px] w-[420px] bg-orchid-500/12" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ink-950" />

        <div className="relative mx-auto grid max-w-edge items-center gap-16 px-6 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:py-28">
          <div>
            <div className="enter inline-flex items-center gap-2.5 rounded-full border border-white/12 bg-white/[0.03] px-4 py-2">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-peri-400 opacity-70" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-peri-300" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-label text-paper/60">
                Mobile studio · Rome, Italy
              </span>
            </div>

            <h1
              style={{ animationDelay: '0.1s' }}
              className="enter display-tight mt-8 text-[52px] text-paper sm:text-[74px] lg:text-[86px]"
            >
              Next-gen mobile
              <br />
              <span className="gradient-text italic">experiences</span>
            </h1>

            <p
              style={{ animationDelay: '0.22s' }}
              className="enter mt-8 max-w-xl text-[17px] leading-relaxed text-paper/65"
            >
              Great technology begins with great people. Parnasa designs, builds and advises on
              mobile products — pairing a team that has shipped before with the kind of care that
              makes an app worth keeping on the home screen.
            </p>

            <div
              style={{ animationDelay: '0.34s' }}
              className="enter mt-10 flex flex-wrap items-center gap-3"
            >
              <a
                href="#services"
                className="group inline-flex items-center gap-2.5 rounded-full bg-brand-sweep px-7 py-3.5 font-mono text-[11px] uppercase tracking-label text-ink-950 transition-transform duration-300 hover:-translate-y-0.5"
              >
                Our services
                <ArrowRight
                  size={14}
                  strokeWidth={2}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/18 px-7 py-3.5 font-mono text-[11px] uppercase tracking-label text-paper transition-colors duration-300 hover:border-peri-400/60 hover:bg-peri-400/[0.08]"
              >
                Talk to the studio
                <ArrowUpRight size={14} strokeWidth={1.8} />
              </a>
            </div>

            <dl
              style={{ animationDelay: '0.5s' }}
              className="enter mt-14 grid max-w-xl grid-cols-1 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/[0.06] sm:grid-cols-3"
            >
              {[
                { term: 'Disciplines', desc: 'Design · Engineering · Consultancy' },
                { term: 'Platforms', desc: 'iOS · Android · Cross-platform' },
                { term: 'Engagement', desc: 'Project · Retainer · Advisory' },
              ].map((item) => (
                <div key={item.term} className="bg-ink-950 px-5 py-5">
                  <dt className="label">{item.term}</dt>
                  <dd className="mt-2 text-[14px] leading-snug text-paper/80">{item.desc}</dd>
                </div>
              ))}
            </dl>
          </div>

          <HeroVisual />
        </div>

        {/* Technology marquee */}
        <div className="relative overflow-hidden border-y border-white/10 bg-ink-900/60 py-4">
          <div className="flex w-max animate-marquee gap-10 whitespace-nowrap">
            {[...MARQUEE, ...MARQUEE].map((item, i) => (
              <span
                key={`${item}-${i}`}
                className="flex items-center gap-10 font-mono text-[10.5px] uppercase tracking-label text-paper/55"
              >
                {item}
                <span className="h-1 w-1 rounded-full bg-peri-400/50" />
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── 01 Studio ────────────────────────────────────────────────────── */}
      <section id="studio" className="relative scroll-mt-20 py-24 lg:py-32">
        <div className="mx-auto max-w-edge px-6 lg:px-10">
          <SectionHeading
            index="01"
            label="The studio"
            title={
              <>
                Great technology begins
                <br />
                with <span className="italic text-peri-300">great people</span>
              </>
            }
            lede="At Parnasa we build apps and technology solutions around the way a business actually works. Small team, direct collaboration, and a habit of turning an idea into something real."
          />

          <div className="mt-16 grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-ink-950">
                <img
                  src="/about-2.jpg"
                  alt="A development team working together at a laptop"
                  loading="lazy"
                  className="duotone relative h-[360px] w-full object-cover lg:h-[440px]"
                />
                <div className="duotone-tint absolute inset-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/45 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-7">
                  <p className="font-mono text-[10px] uppercase tracking-label text-paper/60">
                    Collaboration, not handover
                  </p>
                  <p className="mt-2 font-display text-[26px] leading-tight text-paper">
                    One team, from first sketch to store release
                  </p>
                </div>
              </div>
            </Reveal>

            <div className="space-y-7">
              <Reveal delay={0.06}>
                <p className="text-[16.5px] leading-relaxed text-paper/70">
                  Our team is passionate about creating innovative apps and technology solutions
                  that meet the specific needs of the people who commission them. We work closely
                  with founders, product owners and in-house teams — asking the awkward questions
                  early, when they are still cheap to answer.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="text-[16.5px] leading-relaxed text-paper/70">
                  Making smart decisions starts with having the right tools and the right talent.
                  Parnasa provides the development, innovation and ongoing support you need to
                  create products that hold up in the market — and that your own team can carry
                  forward once we hand over.
                </p>
              </Reveal>

              <Reveal delay={0.18}>
                <div className="grid gap-px overflow-hidden rounded-xl border border-white/10 bg-white/[0.06] sm:grid-cols-2">
                  {[
                    { icon: MapPin, k: 'Based in', v: 'Rome, Italy' },
                    { icon: Users, k: 'Working with', v: 'Founders & product teams' },
                    { icon: Clock, k: 'Availability', v: company.hours },
                    { icon: LifeBuoy, k: 'After launch', v: 'Support & handover' },
                  ].map(({ icon: Icon, k, v }) => (
                    <div key={k} className="flex items-start gap-3 bg-ink-950 px-5 py-5">
                      <Icon size={15} strokeWidth={1.6} className="mt-1 shrink-0 text-peri-400" />
                      <div>
                        <p className="label">{k}</p>
                        <p className="mt-1.5 text-[14.5px] text-paper/80">{v}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>

          {/* Pillars */}
          <div className="mt-20 grid gap-5 md:grid-cols-3">
            {PILLARS.map((pillar, i) => (
              <Reveal key={pillar.title} delay={i * 0.08}>
                <article className="surface surface-hover h-full rounded-2xl p-8">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-peri-400/10 text-peri-300">
                    <pillar.icon size={19} strokeWidth={1.6} />
                  </span>
                  <h3 className="mt-6 font-display text-[25px] leading-tight text-paper">
                    {pillar.title}
                  </h3>
                  <p className="mt-3.5 text-[14.5px] leading-relaxed text-paper/60">{pillar.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 02 Services ──────────────────────────────────────────────────── */}
      <section
        id="services"
        className="relative scroll-mt-20 overflow-hidden border-y border-white/10 bg-ink-900 py-24 lg:py-32"
      >
        <div className="bloom -right-40 top-1/4 h-[520px] w-[520px] bg-peri-600/12" />
        <div className="relative mx-auto max-w-edge px-6 lg:px-10">
          <SectionHeading
            index="02"
            label="Services"
            title={
              <>
                Three ways we
                <br />
                <span className="italic text-peri-300">work with you</span>
              </>
            }
            lede="Design, engineering and consultancy stand on their own or combine into a single engagement. Most projects start with one and grow into another."
          />

          <div className="mt-16 space-y-5">
            {SERVICES.map((service, i) => (
              <Reveal key={service.name} delay={i * 0.07}>
                <article className="surface surface-hover group grid gap-8 rounded-2xl p-8 lg:grid-cols-[0.85fr_1.15fr] lg:p-10">
                  <div>
                    <div className="flex items-center gap-4">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-sweep text-ink-950">
                        <service.icon size={20} strokeWidth={1.8} />
                      </span>
                      <span className="font-mono text-[11px] tracking-label text-paper/55">
                        0{i + 1}
                      </span>
                    </div>
                    <h3 className="display-tight mt-6 text-[36px] text-paper lg:text-[42px]">
                      {service.name}
                    </h3>
                    <p className="mt-4 max-w-md text-[15px] leading-relaxed text-paper/60">
                      {service.lede}
                    </p>
                    <a
                      href="#contact"
                      className="mt-7 inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-label text-peri-300 transition-colors hover:text-paper"
                    >
                      Discuss {service.name.toLowerCase()}
                      <ArrowUpRight size={13} strokeWidth={1.8} />
                    </a>
                  </div>

                  <ul className="grid gap-px self-start overflow-hidden rounded-xl border border-white/10 bg-white/[0.06]">
                    {service.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-3 bg-ink-900 px-6 py-4 text-[14.5px] leading-relaxed text-paper/70"
                      >
                        <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-peri-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 03 Approach ──────────────────────────────────────────────────── */}
      <section id="approach" className="texture-field relative scroll-mt-20 py-24 lg:py-32">
        <div className="mx-auto max-w-edge px-6 lg:px-10">
          <SectionHeading
            index="03"
            label="Approach"
            title={
              <>
                A process you can
                <br />
                <span className="italic text-peri-300">follow along</span>
              </>
            }
            lede="No black box. Five stages, each ending in something you can look at, question and sign off."
          />

          <ol className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/[0.06] lg:grid-cols-5">
            {APPROACH.map((phase, i) => (
              <li key={phase.step} className="group relative bg-ink-950 p-7 transition-colors duration-500 hover:bg-ink-900">
                <Reveal delay={i * 0.06}>
                  <span className="font-mono text-[11px] tracking-label text-peri-400">
                    {phase.step}
                  </span>
                  <h3 className="mt-5 font-display text-[27px] leading-none text-paper">
                    {phase.title}
                  </h3>
                  <p className="mt-4 text-[14px] leading-relaxed text-paper/55">{phase.body}</p>
                  <span className="mt-6 block h-px w-0 bg-brand-sweep transition-all duration-700 group-hover:w-full" />
                </Reveal>
              </li>
            ))}
          </ol>

          {/* Platforms */}
          <div className="mt-24">
            <SectionHeading
              index="04"
              label="Capabilities"
              title={
                <>
                  Built on tools that
                  <br />
                  <span className="italic text-peri-300">age well</span>
                </>
              }
              lede="We choose the stack that suits your product and your team's ability to maintain it — then document the decision."
            />

            <div className="mt-14 grid gap-5 sm:grid-cols-2">
              {PLATFORMS.map((platform, i) => (
                <Reveal key={platform.title} delay={i * 0.07}>
                  <article className="surface surface-hover h-full rounded-2xl p-8">
                    <div className="flex items-start justify-between gap-6">
                      <div>
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-peri-400/10 text-peri-300">
                          <platform.icon size={19} strokeWidth={1.6} />
                        </span>
                        <h3 className="mt-6 font-display text-[27px] leading-tight text-paper">
                          {platform.title}
                        </h3>
                        <p className="mt-3 max-w-sm text-[14.5px] leading-relaxed text-paper/60">
                          {platform.body}
                        </p>
                      </div>
                      <span className="font-mono text-[10.5px] tracking-label text-paper/55">
                        0{i + 1}
                      </span>
                    </div>
                    <div className="mt-7 flex flex-wrap gap-2">
                      {platform.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border border-white/12 px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-label text-paper/55"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials (renders only with real quotes) ─────────────────── */}
      {TESTIMONIALS.length > 0 && (
        <section
          id="testimonials"
          className="relative scroll-mt-20 border-y border-white/10 bg-ink-900 py-24 lg:py-32"
        >
          <div className="mx-auto max-w-edge px-6 lg:px-10">
            <SectionHeading
              index="05"
              label="Testimonials"
              title="In their words"
              align="center"
            />
            <div className="mt-16 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {TESTIMONIALS.map((item, i) => (
                <Reveal key={item.author} delay={i * 0.08}>
                  <figure className="surface h-full rounded-2xl p-8">
                    <Quote size={20} strokeWidth={1.6} className="text-peri-400" />
                    <blockquote className="mt-5 text-[15.5px] leading-relaxed text-paper/75">
                      {item.quote}
                    </blockquote>
                    <figcaption className="mt-6 border-t border-white/10 pt-5">
                      <p className="text-[14.5px] font-semibold text-paper">{item.author}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-label text-paper/55">
                        {item.role}
                      </p>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── 05 Contact ───────────────────────────────────────────────────── */}
      <section
        id="contact"
        className="relative scroll-mt-20 overflow-hidden border-t border-white/10 bg-ink-900 py-24 lg:py-32"
      >
        <div className="bloom -left-32 bottom-0 h-[520px] w-[520px] bg-peri-600/18" />
        <div className="bloom right-0 top-0 h-[380px] w-[380px] bg-orchid-500/10" />

        <div className="relative mx-auto max-w-edge px-6 lg:px-10">
          <div className="grid gap-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <div>
              <SectionHeading
                index="05"
                label="Contacts"
                title={
                  <>
                    Tell us what you
                    <br />
                    <span className="italic text-peri-300">want to build</span>
                  </>
                }
                lede="Write to us with a sentence or two about the product, where it stands today and the timeline you have in mind. A person reads every message and replies."
              />

              <Reveal delay={0.2}>
                <a
                  href={`mailto:${company.email}?subject=Project%20enquiry%20—%20Parnasa%20Apps`}
                  className="group mt-10 inline-flex items-center gap-3 rounded-full bg-brand-sweep px-8 py-4 text-ink-950 transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <Mail size={16} strokeWidth={2} />
                  <span className="font-mono text-[11px] uppercase tracking-label">
                    {company.email}
                  </span>
                  <ArrowUpRight
                    size={15}
                    strokeWidth={2}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              </Reveal>

              <Reveal delay={0.28}>
                <div className="mt-10">
                  <p className="label">Helpful to include</p>
                  <ul className="mt-5 space-y-3">
                    {[
                      'What the app should do, and for whom',
                      'Whether you are starting fresh or improving something live',
                      'Platforms, timing and any budget range you can share',
                    ].map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-3 text-[15px] leading-relaxed text-paper/65"
                      >
                        <Rocket size={14} strokeWidth={1.7} className="mt-1.5 shrink-0 text-peri-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.14}>
              <div className="surface rounded-2xl p-8 lg:p-10">
                <p className="label">Studio address</p>
                <address className="mt-5 not-italic font-display text-[27px] leading-snug text-paper">
                  {company.address.street}
                  <br />
                  {company.address.postalCode} {company.address.city}
                  <br />
                  {company.address.country}
                </address>

                <div className="mt-8 space-y-px overflow-hidden rounded-xl border border-white/10 bg-white/[0.06]">
                  {[
                    { icon: Mail, k: 'Email', v: company.email },
                    { icon: Clock, k: 'Office hours', v: company.hours },
                    { icon: MapPin, k: 'Registered as', v: company.legalName },
                  ].map(({ icon: Icon, k, v }) => (
                    <div key={k} className="flex items-start gap-3 bg-ink-900 px-6 py-5">
                      <Icon size={15} strokeWidth={1.6} className="mt-1 shrink-0 text-peri-400" />
                      <div>
                        <p className="label">{k}</p>
                        <p className="mt-1.5 break-words text-[14.5px] text-paper/80">{v}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="mt-7 text-[13.5px] leading-relaxed text-paper/55">
                  Email is the fastest route to us. We do not collect personal data through this
                  website beyond what you choose to send us — see the{' '}
                  <a
                    href="/privacy"
                    className="text-peri-300 underline decoration-peri-400/40 underline-offset-4 hover:text-paper"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default HomePage;
